import { useMemo, useState } from 'react'
import {
  IconActivity, IconCloudUpload, IconFileText, IconLink, IconNotebook, IconPencil, IconSettings, IconTrash, IconUser,
} from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { useOutletContext } from 'react-router-dom'
import dayjs from 'dayjs'

import { projectApi } from '@/api/endpoints'
import type { ActivityEntry, Project } from '@/api/types'
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States'
import { auditColor, auditDetail, auditLabel } from '@/utils/auditLabels'
import { formatRelative } from '@/utils/format'
import { Avatar, cx } from '@/ui'
import './activity.css'

type Kategori = 'all' | 'notes' | 'files' | 'members'

const ADET = 60

function kategori(action: string): Exclude<Kategori, 'all'> | 'other' {
  if (action.startsWith('NOTE_')) return 'notes'
  if (action.startsWith('FILE_') || action.startsWith('SHARE_')) return 'files'
  if (action.startsWith('MEMBER_') || action.startsWith('PROJECT_TEAM_') || action === 'OWNERSHIP_TRANSFERRED') return 'members'
  return 'other'
}

function simge(action: string) {
  const p = { size: 16, stroke: 1.8 }
  if (action.endsWith('_DELETED') || action.endsWith('_REMOVED')) return <IconTrash {...p} />
  if (action.startsWith('SHARE_')) return <IconLink {...p} />
  if (action === 'FILE_UPLOADED') return <IconCloudUpload {...p} />
  if (action.startsWith('FILE_')) return <IconFileText {...p} />
  if (action === 'NOTE_CREATED') return <IconNotebook {...p} />
  if (action.startsWith('NOTE_')) return <IconPencil {...p} />
  if (kategori(action) === 'members') return <IconUser {...p} />
  if (action.startsWith('PROJECT_')) return <IconSettings {...p} />
  return <IconActivity {...p} />
}

function gunBasligi(iso: string) {
  const d = dayjs(iso)
  if (d.isSame(dayjs(), 'day')) return 'Bugün'
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return `Dün · ${d.format('D MMMM')}`
  return d.format('D MMMM, dddd')
}

/** Proje hareketleri: günlere bölünmüş akış, kategori süzgeci ve haftalık özet. */
export function ActivityTab() {
  const { project } = useOutletContext<{ project: Project }>()
  const [kat, setKat] = useState<Kategori>('all')

  const query = useQuery({
    queryKey: ['activity', project.id],
    queryFn: () => projectApi.activity(project.id, 0, ADET),
  })
  const hepsi = useMemo(() => query.data?.content ?? [], [query.data])

  const gunler = useMemo(() => {
    const out: Array<{ baslik: string; ogeler: ActivityEntry[] }> = []
    for (const e of hepsi.filter((x) => kat === 'all' || kategori(x.action) === kat)) {
      const b = gunBasligi(e.at)
      const son = out[out.length - 1]
      if (son?.baslik === b) son.ogeler.push(e)
      else out.push({ baslik: b, ogeler: [e] })
    }
    return out
  }, [hepsi, kat])

  const ozet = useMemo(() => {
    const hafta = hepsi.filter((e) => dayjs(e.at).isAfter(dayjs().subtract(7, 'day')))
    const say = (k: Exclude<Kategori, 'all'>) => hafta.filter((e) => kategori(e.action) === k).length
    const kisiler = new Map<string, number>()
    hafta.forEach((e) => kisiler.set(e.actorUsername, (kisiler.get(e.actorUsername) ?? 0) + 1))
    return {
      toplam: hafta.length,
      // Yüklenen sayfa haftanın hepsini kapsamıyorsa sayı alt sınırdır.
      eksik: hafta.length === hepsi.length && hepsi.length >= ADET,
      dagilim: [
        { label: 'Notlar', n: say('notes'), renk: 'var(--nx-lime)' },
        { label: 'Dosyalar', n: say('files'), renk: '#ffffff' },
        { label: 'Üyeler', n: say('members'), renk: '#7d8494' },
      ],
      kisiler: [...kisiler].sort((a, b) => b[1] - a[1]).slice(0, 5),
    }
  }, [hepsi])

  if (query.isLoading) return <LoadingState />
  if (query.isError) return <ErrorState error={query.error} onRetry={() => query.refetch()} />
  if (!hepsi.length) {
    return <EmptyState title="Henüz hareket yok" hint="Projede yapılan işlemler burada listelenir." />
  }

  const enCok = Math.max(1, ...ozet.dagilim.map((d) => d.n))

  return (
    <div className="nx-act">
      <section className="nx-panel nx-act-main" aria-labelledby="act-h">
        <div className="nx-act-head">
          <div className="nx-act-head__text">
            <h2 id="act-h">Proje hareketleri</h2>
            <span>Son {hepsi.length} işlem</span>
          </div>
          <div role="radiogroup" aria-label="Süzgeç" className="nx-act-chips">
            {([['all', 'Tümü'], ['notes', 'Notlar'], ['files', 'Dosyalar'], ['members', 'Üyeler']] as const).map(([v, l]) => (
              <button key={v} type="button" role="radio" aria-checked={kat === v} className="nx-act-chip" onClick={() => setKat(v)}>{l}</button>
            ))}
          </div>
        </div>

        <div className="nx-act-feed">
          {gunler.length === 0 && <p className="nx-act-empty">Bu süzgece uyan hareket yok.</p>}
          {gunler.map((g) => (
            <div key={g.baslik} className="nx-act-day">
              <span className="nx-act-day__title">{g.baslik}</span>
              <ol>
                {g.ogeler.map((e) => {
                  const renk = auditColor(e.action)
                  const detay = auditDetail(e.details)
                  return (
                    <li key={e.id} className={cx('nx-act-item', renk === 'red' && 'is-bad', renk === 'green' && 'is-ok')}>
                      <div className="nx-act-item__rail">
                        <span className="nx-act-item__icon" aria-hidden="true">{simge(e.action)}</span>
                        <span className="nx-act-item__line" aria-hidden="true" />
                      </div>
                      <div className="nx-act-item__body">
                        <span className="nx-act-item__title">
                          <span className="nx-act-item__label">{auditLabel(e.action)}</span>
                          {detay && <span className="nx-act-item__detail">{detay}</span>}
                        </span>
                        <span className="nx-act-item__meta nx-nums">{e.actorUsername} · {formatRelative(e.at)}</span>
                      </div>
                    </li>
                  )
                })}
              </ol>
            </div>
          ))}
        </div>
      </section>

      <aside className="nx-act-side" aria-label="Haftalık özet">
        <div className="nx-act-week">
          <span className="nx-act-week__label">Bu hafta</span>
          <span className="nx-act-week__value nx-nums">{ozet.toplam}{ozet.eksik ? '+' : ''} <span>işlem</span></span>
          <div className="nx-act-week__split">
            {ozet.dagilim.map((d) => (
              <div key={d.label}>
                <span><span>{d.label}</span><span className="nx-nums">{d.n}</span></span>
                <span className="nx-act-week__track"><span style={{ width: `${(d.n / enCok) * 100}%`, background: d.renk }} /></span>
              </div>
            ))}
          </div>
        </div>
        {ozet.kisiler.length > 0 && (
          <div className="nx-panel nx-act-people">
            <span className="nx-act-people__title">En etkin üyeler</span>
            {ozet.kisiler.map(([ad, n]) => (
              <div key={ad} className="nx-act-person">
                <Avatar name={ad} size={30} />
                <span className="nx-act-person__name">{ad}</span>
                <span className="nx-act-person__n nx-nums">{n}</span>
              </div>
            ))}
          </div>
        )}
      </aside>
    </div>
  )
}
